import { StarIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { ReactNode } from 'react';

export type tBubbleMetaStatus = {
  text: string;
  /** "error": No entregado / No enviado (INT-1503, INT-1500 U10). "review": En revisión (INT-1500 U5). */
  tone: 'error' | 'review';
};

export type tBubbleMetaProps = {
  /** "2 oct 9:52 p.m." */
  stamp: string;
  /** The same moment in words, for screen readers. */
  spoken: string;
  /** ISO timestamp for the <time> element. */
  dateTime: string;
  editedLabel: string;
  isFavorite?: boolean;
  isEdited?: boolean;
  status?: tBubbleMetaStatus;
  /** Delivery checks of an outgoing message (showCheckStatus). */
  ticks?: ReactNode;
  /**
   * "overlay": pinned to the bottom right of the text, over the spacer.
   * "spacer": an invisible copy at the end of the last line that reserves the
   * overlay's width, so a short message keeps its date on its own line.
   * "block": its own row, for a bubble that does not end in text.
   */
  variant: 'overlay' | 'spacer' | 'block';
};

/**
 * The date and time next to the message, bottom right, as in WhatsApp Web,
 * with what used to live in the header: the star, "Editado", the status words
 * and the delivery checks. The status classes keep their old names because the
 * notice tests and styles key on them.
 */
export default function BubbleMeta(props: tBubbleMetaProps) {
  const { stamp, spoken, dateTime, editedLabel, isFavorite, isEdited, status, ticks, variant } =
    props;
  const isSpacer = variant === 'spacer';
  const time = (
    <time
      className="BubbleMeta__Time"
      dateTime={dateTime}
      aria-label={isSpacer ? undefined : spoken}
      title={isSpacer ? undefined : spoken}
    >
      {stamp}
    </time>
  );

  return (
    <span
      className={classNames('BubbleMeta', `BubbleMeta--${variant}`)}
      aria-hidden={isSpacer || undefined}
      data-testid={isSpacer ? undefined : 'bubble-meta'}
    >
      {isFavorite && <StarIcon className="BubbleMeta__Star" aria-hidden="true" />}
      {isEdited && <span className="BubbleMeta__Edited">{editedLabel}</span>}
      {status ? (
        <span
          className={
            status.tone === 'error'
              ? 'MessageBubbleV2__Header__NotDelivered'
              : 'MessageBubbleV2__Header__InReview'
          }
        >
          {time} · {status.text}
        </span>
      ) : (
        time
      )}
      {isSpacer
        ? ticks && <span className="BubbleMeta__TicksSpace" />
        : ticks && <span className="BubbleMeta__Ticks">{ticks}</span>}
    </span>
  );
}
